import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfWeek, endOfWeek } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from './button';

interface CalendarProps {
  mode?: 'single' | 'range';
  selected?: { from?: Date; to?: Date } | Date | undefined;
  onSelect?: (date: { from?: Date; to?: Date } | Date | undefined) => void;
  numberOfMonths?: number;
  disabled?: (date: Date) => boolean;
  className?: string;
}

export function Calendar({
  mode = 'single',
  selected,
  onSelect,
  numberOfMonths = 1,
  disabled,
  className,
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [hoverDate, setHoverDate] = React.useState<Date | undefined>(undefined);

  const months = React.useMemo(() => {
    const monthsArray: Date[] = [];
    for (let i = 0; i < numberOfMonths; i++) {
      const month = new Date(currentMonth);
      month.setMonth(month.getMonth() + i);
      monthsArray.push(month);
    }
    return monthsArray;
  }, [currentMonth, numberOfMonths]);

  const days = React.useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentMonth]);

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const isInRange = (date: Date) => {
    if (mode !== 'range' || !selected || !('from' in selected)) return false;
    const { from, to } = selected;
    if (!from) return false;
    if (from && !to) {
      return isSameDay(date, from) || (hoverDate && date > from && date <= hoverDate);
    }
    if (from && to) {
      return (date >= from && date <= to) || (hoverDate && date >= from && date <= hoverDate);
    }
    return false;
  };

  const handleDateClick = (date: Date) => {
    if (disabled && disabled(date)) return;

    if (mode === 'single') {
      onSelect?.(date);
    } else if (mode === 'range') {
      const current = selected as { from?: Date; to?: Date } | undefined;
      if (!current?.from || (current.from && current.to)) {
        onSelect?.({ from: date, to: undefined });
      } else if (current.from && !current.to) {
        if (date < current.from) {
          onSelect?.({ from: date, to: current.from });
        } else {
          onSelect?.({ from: current.from, to: date });
        }
      }
    }
  };

  const isSelected = (date: Date) => {
    if (mode === 'single' && selected instanceof Date) {
      return isSameDay(date, selected);
    }
    if (mode === 'range' && selected && 'from' in selected) {
      const { from, to } = selected;
      if (from && isSameDay(date, from)) return true;
      if (to && isSameDay(date, to)) return true;
    }
    return false;
  };

  return (
    <div className={cn('p-3', className)}>
      {numberOfMonths === 1 ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevMonth}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-sm font-semibold">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNextMonth}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div
                key={day}
                className="h-8 w-8 flex items-center justify-center text-xs font-medium text-text-muted"
              >
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isDisabled = disabled?.(day) || isBefore(day, new Date(0)) || (disabled && disabled(day));
              const selected = isSelected(day);
              const inRange = isInRange(day);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => handleDateClick(day)}
                  onMouseEnter={() => mode === 'range' && setHoverDate(day)}
                  onMouseLeave={() => mode === 'range' && setHoverDate(undefined)}
                  disabled={isDisabled}
                  className={cn(
                    'h-8 w-8 flex items-center justify-center text-sm rounded-md transition-colors',
                    !isCurrentMonth && 'text-text-muted opacity-50',
                    isCurrentMonth && !selected && !inRange && 'hover:bg-primary/10',
                    isCurrentMonth && inRange && 'bg-primary/20',
                    isCurrentMonth && selected && 'bg-primary text-white',
                    isToday(day) && !selected && 'font-semibold border border-primary',
                    isDisabled && 'opacity-50 cursor-not-allowed hover:bg-transparent'
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex gap-4">
          {months.map((month) => (
            <div key={month.toISOString()} className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePrevMonth}
                  className="h-8 w-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-sm font-semibold">
                  {format(month, 'MMMM yyyy')}
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNextMonth}
                  className="h-8 w-8"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                  <div
                    key={day}
                    className="h-8 w-8 flex items-center justify-center text-xs font-medium text-text-muted"
                  >
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {eachDayOfInterval({
                  start: startOfWeek(startOfMonth(month)),
                  end: endOfWeek(endOfMonth(month)),
                }).map((day) => {
                  const isCurrentMonth = isSameMonth(day, month);
                  const isDisabled = disabled?.(day) || (disabled && disabled(day));
                  const selected = isSelected(day);
                  const inRange = isInRange(day);

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => handleDateClick(day)}
                      onMouseEnter={() => mode === 'range' && setHoverDate(day)}
                      onMouseLeave={() => mode === 'range' && setHoverDate(undefined)}
                      disabled={isDisabled}
                      className={cn(
                        'h-8 w-8 flex items-center justify-center text-sm rounded-md transition-colors',
                        !isCurrentMonth && 'text-text-muted opacity-50',
                        isCurrentMonth && !selected && !inRange && 'hover:bg-primary/10',
                        isCurrentMonth && inRange && 'bg-primary/20',
                        isCurrentMonth && selected && 'bg-primary text-white',
                        isToday(day) && !selected && 'font-semibold border border-primary',
                        isDisabled && 'opacity-50 cursor-not-allowed hover:bg-transparent'
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
